import { API_BASE } from './apiClient';

const toAbsoluteApiUrl = (path) => {
  const base = String(API_BASE || '').replace(/\/+$/, '');
  let p = String(path || '');
  if (!p.startsWith('/')) p = '/' + p;
  if (/\/api$/i.test(base) && /^\/api(\/|$)/i.test(p)) {
    p = p.replace(/^\/api/i, '');
  }
  return `${base}${p}`;
};

const requestJson = async (path, options) => {
  const baseUrl = toAbsoluteApiUrl(path);

  const doFetch = async (url) => {
    const res = await fetch(url, {
      headers: {
        accept: 'application/json',
        'cache-control': 'no-cache',
        pragma: 'no-cache',
        ...(options?.headers || {})
      },
      cache: 'no-store',
      ...options
    });
    return res;
  };

  let res = await doFetch(baseUrl);
  if (res.status === 304) {
    const sep = baseUrl.includes('?') ? '&' : '?';
    res = await doFetch(`${baseUrl}${sep}__ts=${Date.now()}`);
  }

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.message || data?.erro || text || `Erro ${res.status}`;
    throw new Error(msg);
  }

  return data;
};

const extractList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.Registros)) return data.Registros;
  if (Array.isArray(data?.registros)) return data.registros;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.resultados)) return data.resultados;
  return [];
};

const normalizeHost = (host) => {
  const h = String(host || '').trim().toLowerCase();
  if (!h) return '';
  return h.replace(/^www\./, '').split(':')[0];
};

const normalizeDomainValue = (value) => {
  let v = String(value || '').trim().toLowerCase();
  if (!v) return '';
  v = v.replace(/^https?:\/\//, '');
  v = v.split('/')[0];
  return normalizeHost(v);
};

const resolveI18nLanguageFromSigla = (sigla) => {
  const s = String(sigla || '').trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  // já veio no formato esperado (ex: pt-BR)
  if (/^[a-z]{2}-[a-z]{2}$/i.test(s)) return `${s.split('-')[0].toLowerCase()}-${s.split('-')[1].toUpperCase()}`;
  if (lower === 'pt') return 'pt-PT';
  if (lower === 'pt-pt') return 'pt-PT';
  if (lower === 'pt-br') return 'pt-BR';
  if (lower === 'en') return 'en-US';
  if (lower === 'es') return 'es-ES';
  if (lower === 'fr') return 'fr-FR';
  return null;
};

export async function resolveDomainBootstrapConfig() {
  if (typeof window === 'undefined') return { lang: null, currency: null };

  const hostname = normalizeHost(window.location?.hostname);

  try {
    const [dominiosData, idiomasData, moedasData] = await Promise.all([
      requestJson('/api/dominios-portal?wrap=1'),
      requestJson('/api/idiomas'),
      requestJson('/api/moedas')
    ]);

    const dominios = extractList(dominiosData);
    const idiomas = extractList(idiomasData);
    const moedas = extractList(moedasData);

    const normalizedDominios = dominios
      .filter(Boolean)
      .map((d) => ({
        id: d.codigoDOMINIO ?? d.id ?? d.codigo ?? null,
        dominio: normalizeDomainValue(d.dominioDOMINIO ?? d.dominio ?? ''),
        principal: Boolean(d.principalDOMINIO ?? d.principal),
        idiomaId: d.idiomaDOMINIO ?? d.idioma ?? d.idiomaId ?? d.codigoIDIOMA ?? d.codigoIdioma ?? d.idIdioma,
        moedaId: d.moedaDOMINIO ?? d.moeda ?? d.moedaId ?? d.codigoMOEDA ?? d.codigoMoeda ?? d.idMoeda
      }))
      .filter((d) => d.dominio);

    const current = normalizedDominios.find((d) => d.dominio === hostname);
    const fallback = normalizedDominios.find((d) => d.principal) || normalizedDominios[0] || null;
    const chosen = current || fallback;

    if (!chosen) return { lang: null, currency: null };

    const idioma = idiomas.find((i) => String(i?.codigoIDIOMA ?? i?.id ?? i?.codigo) === String(chosen.idiomaId));
    const sigla = idioma?.siglaIDIOMA ?? idioma?.sigla ?? idioma?.codigoSigla ?? null;

    const moeda = moedas.find((m) => String(m?.codigoMOEDA ?? m?.id ?? m?.codigo) === String(chosen.moedaId));
    const iso = moeda?.isoMOEDA ? String(moeda.isoMOEDA).toUpperCase() : null;

    return {
      lang: resolveI18nLanguageFromSigla(sigla),
      currency: iso
    };
  } catch (_) {
    return { lang: null, currency: null };
  }
}
